import { useCallback, useRef, useState } from 'react';

import { describeFile } from './photo-files';

/** Where focus should go next. Each place takes it once and says so. */
export type PhotoFocusTarget = 'crop' | 'actions' | 'problem' | 'keep';

/** Why the last attempt did not go through. `fileLabel` is null when the file was not the cause. */
export interface PhotoProblem {
  message: string;
  fileLabel: string | null;
  /** Counts up with every failure, so the same message twice is still a new event. */
  attempt: number;
}

/** What the person is told about the photo change: the problem, a spoken update, and where focus goes. */
export function usePhotoFeedback() {
  const [problem, setProblem] = useState<PhotoProblem | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [focusTarget, setFocusTarget] = useState<PhotoFocusTarget | null>(null);
  const attempts = useRef(0);

  const report = useCallback((message: string, file: File | null) => {
    attempts.current += 1;
    setProblem({ message, fileLabel: file ? describeFile(file) : null, attempt: attempts.current });
    setFocusTarget('problem');
  }, []);

  return {
    problem,
    announcement,
    focusTarget,
    report,
    announce: setAnnouncement,
    focusOn: setFocusTarget,
    dismissProblem: useCallback(() => setProblem(null), []),
    focusDone: useCallback(() => setFocusTarget(null), []),
  };
}
